import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequirePermission } from '@/components/auth/RequirePermission';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/layouts/AppShell';
import ClientPortalShell from '@/layouts/ClientPortalShell';
import ClientPortalDashboardPage from '@/pages/clientPortal/ClientPortalDashboardPage';
import ClientPortalInventoryPage from '@/pages/clientPortal/ClientPortalInventoryPage';
import ClientPortalGrnPage from '@/pages/clientPortal/ClientPortalGrnPage';
import ClientPortalDispatchOrdersPage from '@/pages/clientPortal/ClientPortalDispatchOrdersPage';
import ClientPortalInvoicesPage from '@/pages/clientPortal/ClientPortalInvoicesPage';
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import TvDashboardPage from '@/pages/tvDashboard/TvDashboardPage';
import Dashboard from '@/pages/dashboard/Dashboard';
import NotFound from '@/pages/misc/NotFound';
import Unauthorized from '@/pages/misc/Unauthorized';
import ProcessConfigurationPage from '@/pages/administration/processConfiguration/ProcessConfigurationPage';
import MastersPage from '@/pages/masters/MastersPage';
import UserManagementPage from '@/pages/administration/users/UserManagementPage';
import ApprovalMatrixPage from '@/pages/administration/approvalMatrix/ApprovalMatrixPage';
import PurchaseRequestsPage from '@/pages/transactions/purchaseRequest/PurchaseRequestsPage';
import PurchaseOrdersPage from '@/pages/transactions/purchaseOrder/PurchaseOrdersPage';
import GateEntriesPage from '@/pages/transactions/gateEntry/GateEntriesPage';
import GrnPage from '@/pages/transactions/grn/GrnPage';
import QualityControlPage from '@/pages/transactions/qualityControl/QualityControlPage';
import PutawayPage from '@/pages/transactions/putaway/PutawayPage';
import WarehouseTransferReceiptPage from '@/pages/transactions/dispatch/WarehouseTransferReceiptPage';
import MaterialRequestsPage from '@/pages/transactions/materialRequest/MaterialRequestsPage';
import ProductionOrdersPage from '@/pages/transactions/production/ProductionOrdersPage';
import JobWorkOrdersPage from '@/pages/transactions/jobWork/JobWorkOrdersPage';
import JobWorkIssuePage from '@/pages/transactions/jobWork/JobWorkIssuePage';
import JobWorkReceiptPage from '@/pages/transactions/jobWork/JobWorkReceiptPage';
import JobWorkQcPage from '@/pages/transactions/jobWork/JobWorkQcPage';
import JobWorkPutawayPage from '@/pages/transactions/jobWork/JobWorkPutawayPage';
import JobWorkLabelPrintPage from '@/pages/printing/JobWorkLabelPrintPage';
import WeightProductionOrdersPage from '@/pages/transactions/weightProduction/WeightProductionOrdersPage';
import WeightProductionKioskPage from '@/pages/transactions/weightProduction/WeightProductionKioskPage';
import WeightProductionPutawayPage from '@/pages/transactions/weightProduction/WeightProductionPutawayPage';
import WeightProductionJobCompletionPage from '@/pages/transactions/weightProduction/WeightProductionJobCompletionPage';
import WeightProductionIssuePage from '@/pages/transactions/weightProduction/WeightProductionIssuePage';
import WeightProductionLabelPrintPage from '@/pages/printing/WeightProductionLabelPrintPage';
import HardwareConfigurationPage from '@/pages/masters/weightProduction/HardwareConfigurationPage';
import HardwareMonitorPage from '@/pages/masters/weightProduction/HardwareMonitorPage';
import ProductionPickListPage from '@/pages/transactions/production/ProductionPickListPage';
import MaterialIssuePage from '@/pages/transactions/production/MaterialIssuePage';
import ProductionReceiptPage from '@/pages/transactions/production/ProductionReceiptPage';
import ProductionLabelPrintPage from '@/pages/printing/ProductionLabelPrintPage';
import ProductionPutawayPage from '@/pages/transactions/productionPutaway/ProductionPutawayPage';
import PickingPage from '@/pages/transactions/picking/PickingPage';
import DispatchOrdersPage from '@/pages/transactions/dispatch/DispatchOrdersPage';
import DispatchPickListPage from '@/pages/transactions/dispatch/DispatchPickListPage';
import DispatchMaterialIssuePage from '@/pages/transactions/dispatch/DispatchMaterialIssuePage';
import PackingPage from '@/pages/transactions/packing/PackingPage';
import LoadingPage from '@/pages/transactions/loading/LoadingPage';
import GateExitPage from '@/pages/transactions/gateExit/GateExitPage';
import StockAdjustmentsPage from '@/pages/transactions/stockAdjustment/StockAdjustmentsPage';
import StockTransfersPage from '@/pages/transactions/stockTransfer/StockTransfersPage';
import CycleCountsPage from '@/pages/transactions/cycleCount/CycleCountsPage';
import ReturnNotesPage from '@/pages/transactions/returnNote/ReturnNotesPage';
import InventoryReportsPage from '@/pages/reports/InventoryReportsPage';
import TraceabilityReportsPage from '@/pages/reports/TraceabilityReportsPage';
import ProcurementReportsPage from '@/pages/reports/ProcurementReportsPage';
import InboundQualityReportsPage from '@/pages/reports/InboundQualityReportsPage';
import OutboundReportsPage from '@/pages/reports/OutboundReportsPage';
import ProductionReportsPage from '@/pages/reports/ProductionReportsPage';
import JobWorkReportsPage from '@/pages/reports/JobWorkReportsPage';
import WeightProductionReportsPage from '@/pages/reports/WeightProductionReportsPage';
import AdjustmentsReportsPage from '@/pages/reports/AdjustmentsReportsPage';
import ApprovalsReportsPage from '@/pages/reports/ApprovalsReportsPage';
import ClientSummaryReportsPage from '@/pages/reports/ClientSummaryReportsPage';
import LabelTemplateList from '@/pages/labelDesigner/LabelTemplateList';
import LabelTemplateDesigner from '@/pages/labelDesigner/LabelTemplateDesigner';
import GrnLabelPrintPage from '@/pages/printing/GrnLabelPrintPage';
import PackingLabelPrintPage from '@/pages/printing/PackingLabelPrintPage';
import MyProfilePage from '@/pages/profile/MyProfilePage';
import BillingPage from '@/pages/transactions/billing/BillingPage';
import CompaniesConsolePage from '@/pages/administration/companies/CompaniesConsolePage';
import ActivityLogPage from '@/pages/administration/activityLog/ActivityLogPage';
import CompanyProcessConfigurationPage from '@/pages/administration/companies/CompanyProcessConfigurationPage';
import { PERMISSIONS, ROLES } from '@/types/auth';

// A Client Portal login has no business in the internal AppShell (their permission set is
// deliberately just the 5 narrow ClientPortal*View codes) — redirect them straight to their own
// shell rather than letting them hit a staff page that will just fail its own permission check.
function InternalAppGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.clientId) {
    return <Navigate to="/portal/dashboard" replace />;
  }
  return <>{children}</>;
}

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.clientId ? '/portal/dashboard' : '/dashboard'} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/tv-dashboard"
        element={
          <RequirePermission>
            <TvDashboardPage />
          </RequirePermission>
        }
      />
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route
        element={
          <RequirePermission>
            <InternalAppGate>
              <AppShell />
            </InternalAppGate>
          </RequirePermission>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<MyProfilePage />} />

        {/* One page, one route — every master (Company, Warehouse, Material, Supplier, Customer,
            and whatever's added next) is a tab inside MastersPage, not its own route. Permission
            gating happens per tab inside that page, not here, since a user might only have
            access to some of them. */}
        <Route path="/masters" element={<MastersPage />} />
        <Route path="/masters/company" element={<Navigate to="/masters" replace />} />
        <Route path="/masters/warehouses" element={<Navigate to="/masters" replace />} />
        <Route path="/masters/materials" element={<Navigate to="/masters" replace />} />
        <Route
          path="/admin/companies"
          element={
            <RequirePermission role={ROLES.SUPER_ADMIN}>
              <CompaniesConsolePage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/companies/:companyId/process-configuration"
          element={
            <RequirePermission role={ROLES.SUPER_ADMIN}>
              <CompanyProcessConfigurationPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/activity-log"
          element={
            <RequirePermission role={ROLES.SUPER_ADMIN}>
              <ActivityLogPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/process-configuration"
          element={
            <RequirePermission permission={PERMISSIONS.PROCESS_CONFIGURATION_VIEW}>
              <ProcessConfigurationPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequirePermission permission={PERMISSIONS.USER_VIEW}>
              <UserManagementPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/approval-matrix"
          element={
            <RequirePermission permission={PERMISSIONS.APPROVAL_MATRIX_VIEW}>
              <ApprovalMatrixPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/purchase-requests"
          element={
            <RequirePermission permission={PERMISSIONS.PURCHASE_REQUEST_VIEW}>
              <PurchaseRequestsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/purchase-orders"
          element={
            <RequirePermission permission={PERMISSIONS.PURCHASE_ORDER_VIEW}>
              <PurchaseOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/gate-entries"
          element={
            <RequirePermission permission={PERMISSIONS.GATE_ENTRY_VIEW}>
              <GateEntriesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/grn"
          element={
            <RequirePermission permission={PERMISSIONS.GRN_VIEW}>
              <GrnPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/quality-control"
          element={
            <RequirePermission permission={PERMISSIONS.QC_VIEW}>
              <QualityControlPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/putaway"
          element={
            <RequirePermission permission={PERMISSIONS.PUTAWAY_VIEW}>
              <PutawayPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/warehouse-transfer-receipts"
          element={
            <RequirePermission permission={PERMISSIONS.PICKING_VIEW}>
              <WarehouseTransferReceiptPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/material-requests"
          element={
            <RequirePermission permission={PERMISSIONS.MATERIAL_REQUEST_VIEW}>
              <MaterialRequestsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/production-orders"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <ProductionOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-orders"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-issue"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkIssuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-receipt"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkReceiptPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-qc"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkQcPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-label-printing"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkLabelPrintPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/job-work-putaway"
          element={
            <RequirePermission permission={PERMISSIONS.JOB_WORK_VIEW}>
              <JobWorkPutawayPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/weight-production-plan"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <WeightProductionOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/weight-production-issue"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <WeightProductionIssuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/weight-production-kiosk"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_EXECUTE}>
              <WeightProductionKioskPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/weight-production-job-completion"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <WeightProductionJobCompletionPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/weight-production-putaway"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <WeightProductionPutawayPage />
            </RequirePermission>
          }
        />
        <Route
          path="/printing/weight-production-label-print"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <WeightProductionLabelPrintPage />
            </RequirePermission>
          }
        />
        <Route
          path="/masters/hardware-configuration"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <HardwareConfigurationPage />
            </RequirePermission>
          }
        />
        <Route
          path="/masters/hardware-monitor"
          element={
            <RequirePermission permission={PERMISSIONS.WEIGHT_PRODUCTION_VIEW}>
              <HardwareMonitorPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/production-pick-list"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <ProductionPickListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/material-issue"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <MaterialIssuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/production-receipt"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <ProductionReceiptPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/production-label-printing"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <ProductionLabelPrintPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/production-putaway"
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTION_VIEW}>
              <ProductionPutawayPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/picking"
          element={
            <RequirePermission permission={PERMISSIONS.PICKING_VIEW}>
              <PickingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/dispatch-orders"
          element={
            <RequirePermission permission={PERMISSIONS.DISPATCH_ORDER_VIEW}>
              <DispatchOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/dispatch-pick-list"
          element={
            <RequirePermission permission={PERMISSIONS.PICKING_VIEW}>
              <DispatchPickListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/dispatch-material-issue"
          element={
            <RequirePermission permission={PERMISSIONS.PICKING_VIEW}>
              <DispatchMaterialIssuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/packing"
          element={
            <RequirePermission permission={PERMISSIONS.PACKING_VIEW}>
              <PackingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/packing-label-printing"
          element={
            <RequirePermission permission={PERMISSIONS.PACKING_VIEW}>
              <PackingLabelPrintPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/loading"
          element={
            <RequirePermission permission={PERMISSIONS.LOADING_VIEW}>
              <LoadingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/gate-exit"
          element={
            <RequirePermission permission={PERMISSIONS.GATE_EXIT_VIEW}>
              <GateExitPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/stock-adjustment"
          element={
            <RequirePermission permission={PERMISSIONS.STOCK_ADJUSTMENT_VIEW}>
              <StockAdjustmentsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/stock-transfers"
          element={
            <RequirePermission permission={PERMISSIONS.STOCK_TRANSFER_VIEW}>
              <StockTransfersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/cycle-counts"
          element={
            <RequirePermission permission={PERMISSIONS.CYCLE_COUNT_VIEW}>
              <CycleCountsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/returns"
          element={
            <RequirePermission permission={PERMISSIONS.RETURN_NOTE_VIEW}>
              <ReturnNotesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/billing"
          element={
            <RequirePermission permission={PERMISSIONS.BILLING_VIEW}>
              <BillingPage />
            </RequirePermission>
          }
        />
        <Route path="/reports" element={<Navigate to="/reports/inventory" replace />} />
        <Route
          path="/reports/inventory"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <InventoryReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/traceability"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <TraceabilityReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/procurement"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <ProcurementReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/inbound-quality"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <InboundQualityReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/outbound"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <OutboundReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/production"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <ProductionReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/job-work"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <JobWorkReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/weight-production"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <WeightProductionReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/adjustments"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <AdjustmentsReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/approvals"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <ApprovalsReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/client-summary"
          element={
            <RequirePermission permission={PERMISSIONS.REPORT_VIEW}>
              <ClientSummaryReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/label-designer"
          element={
            <RequirePermission permission={PERMISSIONS.LABEL_TEMPLATE_VIEW}>
              <LabelTemplateList />
            </RequirePermission>
          }
        />
        <Route
          path="/label-designer/:id"
          element={
            <RequirePermission permission={PERMISSIONS.LABEL_TEMPLATE_VIEW}>
              <LabelTemplateDesigner />
            </RequirePermission>
          }
        />
        <Route
          path="/transactions/label-printing"
          element={
            <RequirePermission permission={PERMISSIONS.LABEL_TEMPLATE_VIEW}>
              <GrnLabelPrintPage />
            </RequirePermission>
          }
        />
      </Route>

      <Route
        element={
          <RequirePermission role={ROLES.CLIENT_PORTAL}>
            <ClientPortalShell />
          </RequirePermission>
        }
      >
        <Route path="/portal/dashboard" element={<ClientPortalDashboardPage />} />
        <Route path="/portal/inventory" element={<ClientPortalInventoryPage />} />
        <Route path="/portal/grn" element={<ClientPortalGrnPage />} />
        <Route path="/portal/dispatch-orders" element={<ClientPortalDispatchOrdersPage />} />
        <Route path="/portal/invoices" element={<ClientPortalInvoicesPage />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
